<template>
    <section class="timetable">
        <header v-if="activeRoute?.length && filteredDepartures?.length" class="timetable__header">
            <div class="timetable__title">
                <h2>{{ filteredDepartures[0].routeName }}</h2>
                <VBtn @click="toggleActiveRoute" color="accent" class="v-btn--circle">
                    <SvgIcon icon="carbon/arrows-horizontal" width="1.5rem" height="1.5rem" />
                </VBtn>
            </div>
            <VBtn color="secondary" @click="printTimetable">
                <span>{{ $t(`timetable.download`) }}</span>
                <i class="fa fa-download"></i>
            </VBtn>
        </header>
        <TimetableDates @selectedDate="setSelectedDate" />
        <TimetableTable 
        	v-if="routeCode" 
        	:departures="filteredDepartures" 
        	:isLoading="isFetching" 
        />
        <div v-else class="timetable__warning">
            <p>{{ $t(`timetable.warnings.routes`) }}</p>
        </div>
    </section>
</template>

<script lang="ts" setup>
import SvgIcon from "@/components/icons/SvgIcon.vue";
import { YYYYMMDD } from "@/helpers/dateHelpers";
import { startOfToday, format } from "date-fns";
import { VBtn } from "v-ferry-components";
import { computed, ref, watch } from "vue";
import { getQueryParamValue } from "@/helpers/urlHelper";
import useTimetableQuery from "../composables/useTimetableQuery";
import TimetableDates from "./TimetableDates.vue";
import TimetableTable from "./TimetableTable.vue";

const activeRoute = ref<string>();

const routeCode = (document.getElementById("timetable-container")?.getAttribute("data-route") as string) ?? getQueryParamValue("route");

const { data: departures, isFetching } = useTimetableQuery(routeCode);

const selectedDate = ref<string>(format(startOfToday(), 'yyyy-MM-dd'));

const filteredDepartures = computed(() => departures.value?.filter((departure) => 
    YYYYMMDD(departure.departureDateTime) === selectedDate.value &&
    departure.routeCode == activeRoute.value
));

function printTimetable() {
    window.print();
}

const setSelectedDate = (date: Date) => {
    selectedDate.value = format(date, 'yyyy-MM-dd');
};

// Switches the first 3 and last 3 letters of the activeRoute
function toggleActiveRoute() {
    if (activeRoute.value) {
        const half = Math.floor(activeRoute.value.length / 2);
        activeRoute.value = activeRoute.value.slice(half) + activeRoute.value.slice(0, half);
    }
}

// Make sure that activeRoute is set when there's a routeCode
watch(() => routeCode, (value) => 
    { activeRoute.value = value },
    { immediate: true }
)
</script>

<style lang="scss" scoped>
.timetable {
    $prefix: ".timetable";

    display: flex;
    flex-direction: column;
    font-size: 1rem;

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #fff;
        row-gap: 1.25rem;

        h1 {
            &.accent {
                color: var(--c-accent);
            }
        }

        @include media-breakpoint-down(md) {
            flex-direction: column;
            align-items: flex-start;
        }
    }

    &__title {
        display: flex;
        align-items: center;
        column-gap: 20px;
    }

    :deep() {
        .v-btn {
            $prefix: ".v-btn";

            &#{$prefix}--secondary {
                display: flex;
                align-items: center;
                padding: 0 1rem;
                height: calc(var(--v-btn-height) - 0.5rem);
                font-size: 0.875rem;
            }

            &--circle {
                padding: 0;
                width: calc(var(--v-btn-height) - 0.5rem);
                height: calc(var(--v-btn-height) - 0.5rem);
                border-radius: 100%;
            }

            &__content {
                display: flex;
                align-items: center;
                gap: 5px;

                span {
                    line-height: 1;
                }
            }

            @media print {
                display: none;
            }
        }
    }

    &__warning {
        display: flex;
        justify-content: center;
        padding: 10px 0;

        p {
            margin: 0;
            color: var(--c-danger);
            font-size: 1rem;
        }
    }
}
</style>
